import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { logUserAction } from '@/utils/analyticsUtils';

/**
 * Hook for automatically tracking user activity
 * This hook will update the user's last activity timestamp whenever they interact with the app
 */
export const useActivityTracker = () => {
  const location = useLocation();
  const lastActivityTime = useRef<number>(Date.now());
  const activityTracked = useRef<boolean>(false);
  const ACTIVITY_THRESHOLD_MS = 60000; // Only update last_update once per minute max
  
  // Function to handle user interactions
  const handleUserActivity = async () => {
    const now = Date.now();
    
    // Only track activity if we haven't tracked in the threshold period
    if (now - lastActivityTime.current > ACTIVITY_THRESHOLD_MS) {
      lastActivityTime.current = now;
      
      try {
        // Get the current user
        const { data } = await supabase.auth.getUser();
        const userId = data.user?.id;
        
        if (userId) {
          // Log the user action which will update the last_update timestamp
          await logUserAction('user_activity', 'ActivityTracker', location.pathname, {
            activity_type: 'user_interaction'
          }, undefined, userId);
          
          activityTracked.current = true;
        }
      } catch (error) {
        console.error('Error tracking user activity:', error);
      }
    }
  };
  
  // Track page navigation
  useEffect(() => {
    const trackPageNavigation = async () => {
      try {
        // Get the current user
        const { data } = await supabase.auth.getUser();
        const userId = data.user?.id;
        
        if (userId) {
          // Log the page view which will update the last_update timestamp
          await logUserAction('page_view', 'ActivityTracker', location.pathname, {
            activity_type: 'page_navigation'
          }, undefined, userId);
          
          activityTracked.current = true;
        }
      } catch (error) {
        console.error('Error tracking page navigation:', error);
      }
    };
    
    trackPageNavigation();
  }, [location.pathname]);
  
  // Set up event listeners for user interactions
  useEffect(() => {
    // Add event listeners for common user interactions
    document.addEventListener('click', handleUserActivity);
    document.addEventListener('keypress', handleUserActivity);
    document.addEventListener('scroll', handleUserActivity);
    document.addEventListener('mousemove', handleUserActivity);
    
    // Clean up event listeners on unmount
    return () => {
      document.removeEventListener('click', handleUserActivity);
      document.removeEventListener('keypress', handleUserActivity);
      document.removeEventListener('scroll', handleUserActivity);
      document.removeEventListener('mousemove', handleUserActivity);
    };
  }, []);
  
  return { activityTracked: activityTracked.current };
}; 